import React, { useEffect, useMemo, useState } from "react";
import {
  IonContent,
  IonFab,
  IonFabButton,
  IonIcon,
  IonPage,
  useIonToast,
  getPlatforms,
  useIonRouter,
  useIonActionSheet,
} from "@ionic/react";
import {
  getDiscountedPrice,
  getPrice,
  GreeterEvent,
  ImageAsset,
  Product,
  Theme,
  Venue,
} from "@greeter/core";
import { EditCard } from "@greeter-venue/components/EditCard";
import { CreateCard } from "@greeter-venue/components/CreateCard";
import {
  AspectRatios,
  Button,
  Carousel,
  Container,
  LazyCoverImage,
  PageCover,
  TitledSection,
  VenueDescriptionCard,
} from "@greeter/matter";
import { EditProductCard } from "@greeter-venue/components/EditProductCard";
import { Routes } from "@greeter-venue/utility/Routes";
import { useMeasure } from "@greeter-venue/utility/Hooks";
import { Share } from "@capacitor/share";
import { Capacitor } from "@capacitor/core";
import { shareSocialOutline } from "ionicons/icons";
import { FeatureFlag, useLocalFeatureFlag } from "@greeter/local-feature-flags";
import StripeNoticeBar from "@greeter-venue/components/StripeNoticeBar";
import { IconHanger } from "@tabler/icons-react";
import css from "./VenuePage.module.scss";

function EventCarousel({
  events,
  onCreate,
  onDuplicate,
  onEdit,
}: {
  events: GreeterEvent[];
  onCreate: () => void;
  onDuplicate: (e: GreeterEvent) => void;
  onEdit: (e: GreeterEvent) => void;
}) {
  const ratio: AspectRatios = "16:9";
  const cardWidth = "16rem";
  const [actionSheet] = useIonActionSheet();

  return (
    <Carousel className={css.Carousel}>
      <CreateCard
        key="create-event"
        style={{ minWidth: cardWidth }}
        onClick={onCreate}
        ratio={ratio}
        title={"Opret event"}
      />
      {events.map((e) => (
        <EditCard
          key={e.id}
          style={{
            minWidth: cardWidth,
            maxWidth: cardWidth,
            height: "fit-content",
          }}
          title={e.name}
          onEdit={() => {
            actionSheet([
              {
                text: "Rediger",
                handler() {
                  onEdit(e);
                },
              },
              {
                text: "Dupliker",
                handler() {
                  onDuplicate(e);
                },
              },
              { text: "Annuller", role: "cancel", data: { action: "cancel" } },
            ]);
          }}
          subTitle={e.description}
          imageUrl={ImageAsset.findUriWithSizeOrDefault(
            e.coverAsset,
            "16x9-w512"
          )}
          ratio={ratio}
        />
      ))}
    </Carousel>
  );
}

function ProductCarousel({
  products,
  onEdit,
  onCreate,
}: {
  products: Product[];
  onCreate: () => void;
  onEdit: (p: Product) => void;
}) {
  const ratio: AspectRatios = "4:3";
  const sortedProducts = useMemo(
    () => Product.sortByPriceAndPriority(products),
    [products]
  );

  return (
    <Carousel>
      <CreateCard
        key="create-product"
        data-testid="create-bundle"
        onClick={onCreate}
        title="Opret bordpakke"
      />
      {sortedProducts.map((p, i) => {
        return (
          <EditProductCard
            key={p.id}
            data-testid={`edit-bundle-${i}`}
            title={p.title}
            subTitle={p.subTitle}
            discountPrice={
              p.discountPercentage ? getDiscountedPrice(p) : undefined
            }
            price={p.price}
            imageUrl={
              p.coverAsset
                ? ImageAsset.findUriWithSizeOrDefault(p.coverAsset, "4x3-w512")
                : p.coverUrl ?? ""
            }
            ratio={ratio}
            style={{ width: "10rem" }}
            onEdit={() => onEdit(p)}
          />
        );
      })}
    </Carousel>
  );
}

export type VenuePageProps = {
  wardrobeEnabled?: boolean;
  venue: Venue;
  themes: Theme[];
  products: Product[];
  events: GreeterEvent[];
  isStripePaymentAccountComplete?: boolean;
  onCompleteStripePaymentAccount?: () => void | Promise<void>;
};

export function VenuePage({
  wardrobeEnabled = false,
  venue,
  themes,
  products,
  events,
  isStripePaymentAccountComplete,
  onCompleteStripePaymentAccount,
}: VenuePageProps) {
  const history = useIonRouter();
  const [{ ref }, measure] = useMeasure<HTMLDivElement>();
  const [noticeBarPadding, setNoticeBarPadding] = useState("0px");

  useEffect(() => {
    if (measure) {
      setNoticeBarPadding(`${measure.height + 15}px`);
    }
  }, [measure]);

  const [toast] = useIonToast();
  const showStripeNotice =
    isStripePaymentAccountComplete !== undefined && !isStripePaymentAccountComplete;

  return (
    <IonPage>
      <IonContent className={css.VenuePage}>
        <div
          style={{
            padding: `4rem 0 calc(${noticeBarPadding} + ${wardrobeEnabled ? "80px" : "0px"})`,
            transition: "100ms",
          }}
        >
          <PageCover>
            <LazyCoverImage
              src={ImageAsset.findUriWithSizeOrDefault(
                venue.coverAsset,
                "16x9-w1024"
              )}
              blur
              fade
            />
          </PageCover>
          <Container>
            <VenueDescriptionCard
              venue={venue}
              editButton={true}
              themes={themes}
            />
            <div className={css.DescriptionBtnContainer}>
              <Button
                data-testid="edit-venue"
                onClick={() => history.push(Routes.mainGroup.editVenue.route())}
                className={css.Button}
              >
                Redigér info
              </Button>
              <Button
                className={css.Button}
                onClick={async () => {
                  const opts = {
                    title: `Book dit bord på ${venue.name}`,
                    url: venue.customUri
                      ? venue.customUri
                      : `https://app.greeter.dk/venues/${venue.id}`,
                  };

                  // We check for it to be desktop since the share functionality
                  // on Safari is absolutely horrendous.
                  // and so if we detect it, we should simply copy the text to
                  // clipboard instead.
                  const isDesktop = new Set(getPlatforms()).has("desktop");

                  if (Capacitor.isNativePlatform()) {
                    Share.share(opts);
                  } else if (navigator.share && !isDesktop) {
                    await navigator.share(opts);
                  } else if (navigator.clipboard) {
                    navigator.clipboard.writeText(opts.url);
                    toast("📋 Booking link kopieret", 1000);
                  } else {
                    toast("Kunne ikke kopiere linket", 1000);
                  }
                }}
              >
                <div className={css.ShareButton}>
                  <IonIcon icon={shareSocialOutline} />{" "}
                  <span style={{ fontWeight: "bold" }}>Del link</span>
                </div>
              </Button>
            </div>
            <TitledSection
              data-testid="bundles"
              title="Bordpakker"
              className={css.TitledSection}
            >
              <ProductCarousel
                onCreate={() => {
                  history.push(Routes.mainGroup.venueTab.editBundle.route({}));
                }}
                onEdit={(p) => {
                  history.push(
                    Routes.mainGroup.venueTab.editBundle.route({
                      bundleId: p.id,
                    }),
                    "forward",
                    "push"
                  );
                }}
                products={products}
              />
            </TitledSection>
            <TitledSection title="Events" className={css.TitledSection}>
              <EventCarousel
                onCreate={() =>
                  history.push(Routes.mainGroup.editEvent.route())
                }
                onDuplicate={(e) =>
                  history.push(
                    Routes.mainGroup.editEvent.route({
                      id: e.id,
                      duplicating: true,
                    })
                  )
                }
                onEdit={(e) =>
                  history.push(Routes.mainGroup.editEvent.route({ id: e.id }))
                }
                events={events.filter((e) => e.endsAt >= new Date())}
              />
            </TitledSection>
          </Container>
        </div>
        {wardrobeEnabled && (
          <IonFab
            horizontal="end"
            vertical="bottom"
            slot="fixed"
            style={{ bottom: showStripeNotice ? noticeBarPadding : undefined }}
          >
            <IonFabButton
              data-testid="venue-wardrobe"
              aria-label="Åbn garderobe"
              title="Garderobe"
              onClick={() =>
                history.push(Routes.mainGroup.venueTab.wardrobe.route())
              }
            >
              <IconHanger size={26} aria-hidden="true" />
            </IonFabButton>
          </IonFab>
        )}
      </IonContent>
      {showStripeNotice && (
        <StripeNoticeBar
          ref={ref}
          onClick={async () => {
            if (onCompleteStripePaymentAccount)
              await onCompleteStripePaymentAccount();
          }}
        />
      )}
    </IonPage>
  );
}
